import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { usePermesso } from './PermessiContext';

interface SocietaItem { id: number; nome: string; coloreAccento?: string; }

export interface BloccoContesto {
  /** Etichetta human-readable della scheda/record (es. "Offerta Capitolato OFC-2024-001"). */
  label: string;
  /** societaId del record (per coerenza futura, non usato per la logica di blocco che è "sempre nega"). */
  recordSocietaId: number;
}

interface SocietaCtx {
  societaAttiva: SocietaItem | null;
  listaSocieta: SocietaItem[];
  setSocietaAttiva: (s: SocietaItem | null) => void;
  /** true quando l'utente ha una sola società → combo disabilitata */
  isSocietaFissa: boolean;
  loading: boolean;
  coloreAccento: string | null;
  /** true quando deve essere mostrata la modale di conferma al login */
  showLoginConfirm: boolean;
  /** Chiama questa funzione per chiudere la modale di login (dopo Continua) */
  consumeLoginConfirm: () => void;
  /**
   * Richiede un cambio di società. Apre la modale di conferma.
   */
  richiediCambioSocieta: (target: SocietaItem | null) => void;
  /** Società target in attesa di conferma dopo richiediCambioSocieta */
  pendingSocieta: SocietaItem | null;
  /** true quando c'è un cambio in attesa (anche verso null = "Tutte le società") */
  pendingSocietaActive: boolean;
  /** Conferma il cambio pendente (chiude la modale) */
  confermaCambio: () => void;
  /** Annulla il cambio pendente (chiude la modale) */
  annullaCambio: () => void;
  /**
   * Indica che l'utente è su una scheda /:id di uno specifico record.
   * Se valorizzato, il cambio società è BLOCCATO — la modale lo segnala.
   */
  bloccoContesto: BloccoContesto | null;
  /** Registra un blocco (chiamato da useBloccoRecordContesto). */
  registraBloccoContesto: (b: BloccoContesto) => void;
  /** Rimuove il blocco (chiamato da useBloccoRecordContesto al unmount). */
  rimuoviBloccoContesto: () => void;
}

const SocietaContext = createContext<SocietaCtx>({
  societaAttiva: null,
  listaSocieta: [],
  setSocietaAttiva: () => {},
  isSocietaFissa: false,
  loading: true,
  coloreAccento: null,
  showLoginConfirm: false,
  consumeLoginConfirm: () => {},
  richiediCambioSocieta: () => {},
  pendingSocieta: null,
  pendingSocietaActive: false,
  confermaCambio: () => {},
  annullaCambio: () => {},
  bloccoContesto: null,
  registraBloccoContesto: () => {},
  rimuoviBloccoContesto: () => {},
});

const LS_KEY = 'bop_societa_attiva';

export function SocietaProvider({ children }: { children: React.ReactNode }) {
  const { utente, isSuperadmin, loading: permLoading, setSocietaContesto } = usePermesso();

  const [listaSocieta, setListaSocieta] = useState<SocietaItem[]>([]);
  const [societaAttiva, setSocietaAttivaInterno] = useState<SocietaItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLoginConfirm, setShowLoginConfirm] = useState(false);
  const [pendingSocieta, setPendingSocieta] = useState<SocietaItem | null>(null);
  const [pendingSocietaActive, setPendingSocietaActive] = useState(false);
  const [bloccoContesto, setBloccoContesto] = useState<BloccoContesto | null>(null);

  useEffect(() => {
    if (permLoading) return;
    setLoading(true);
    let cancelled = false;

    const doLoad = async () => {
      try {
        let lista: SocietaItem[];

        if (isSuperadmin) {
          const data = await apiFetch<{ societa: SocietaItem[] }>('/api/societa');
          lista = data.societa ?? [];
        } else {
          lista = (utente?.societa ?? []) as SocietaItem[];
        }

        if (cancelled) return;
        setListaSocieta(lista);

        // Auto-select unica società
        if (lista.length === 1) {
          setSocietaAttivaInterno(lista[0]);
          return;
        }

        // Per non-SUPERADMIN con più società: forza una società valida
        // (non possono lavorare in modalità "Tutte le società")
        if (!isSuperadmin && lista.length > 1) {
          // Ripristina selezione da localStorage
          const saved = localStorage.getItem(LS_KEY);
          if (saved) {
            try {
              const parsed = JSON.parse(saved) as SocietaItem;
              const found = lista.find(s => s.id === parsed.id);
              if (found) {
                setSocietaAttivaInterno(found);
                return;
              }
            } catch (_) {}
          }
          // Nessuna selezione salvata → forza la prima
          setSocietaAttivaInterno(lista[0]);
          return;
        }

        // Per SUPERADMIN: ripristina da localStorage se presente
        const saved = localStorage.getItem(LS_KEY);
        if (saved) {
          try {
            const parsed = JSON.parse(saved) as SocietaItem;
            const found = lista.find(s => s.id === parsed.id);
            if (found) setSocietaAttivaInterno(found);
          } catch (_) {}
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    doLoad();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permLoading, isSuperadmin]);

  // Pubblica la società attiva nel context permessi: `can()` senza societaId
  // deve valutare i permessi di QUESTA società, non l'unione di tutte
  // (vedi PermessiContext). Il flusso è dal basso verso l'alto perché
  // SocietaProvider è figlio di PermessiProvider.
  useEffect(() => {
    setSocietaContesto(societaAttiva?.id ?? null, !loading && !permLoading);
  }, [societaAttiva, loading, permLoading, setSocietaContesto]);

  // Consuma flag di login per mostrare la modale di conferma società
  useEffect(() => {
    if (loading || permLoading) return;
    const needs = sessionStorage.getItem('bop_login_needs_confirm');
    if (!needs) return;
    // Mostra solo se utente ha 2+ società o è SUPERADMIN
    const multi = listaSocieta.length >= 2;
    if (multi || isSuperadmin) {
      setShowLoginConfirm(true);
    }
    // In ogni caso consumo il flag (anche per single-società)
    sessionStorage.removeItem('bop_login_needs_confirm');
  }, [loading, permLoading, listaSocieta.length, isSuperadmin]);

  const setSocietaAttiva = (s: SocietaItem | null) => {
    setSocietaAttivaInterno(s);
    if (s) localStorage.setItem(LS_KEY, JSON.stringify(s));
    else localStorage.removeItem(LS_KEY);
  };

  const richiediCambioSocieta = (target: SocietaItem | null) => {
    // Se la società richiesta è identica a quella attuale, no-op
    const stessoId = (target?.id ?? null) === (societaAttiva?.id ?? null);
    if (stessoId) return;
    setPendingSocieta(target);
    setPendingSocietaActive(true);
  };

  const confermaCambio = () => {
    setSocietaAttiva(pendingSocieta);
    setPendingSocieta(null);
    setPendingSocietaActive(false);
  };

  const annullaCambio = () => {
    setPendingSocieta(null);
    setPendingSocietaActive(false);
  };

  const consumeLoginConfirm = () => {
    setShowLoginConfirm(false);
  };

  // Memoizzate con useCallback: sono usate come dependency di useEffect
  // nell'hook useBloccoRecordContesto. Senza memoizzazione ad ogni render
  // del Provider cambierebbe la loro reference e l'effect entrerebbe in
  // loop (registra -> setState -> re-render Provider -> nuove refs ->
  // cleanup rimuovi -> setState -> re-render -> ...), congelando la UI.
  const registraBloccoContesto = useCallback((b: BloccoContesto) => {
    setBloccoContesto(b);
  }, []);

  const rimuoviBloccoContesto = useCallback(() => {
    setBloccoContesto(null);
  }, []);

  const coloreAccento = societaAttiva?.coloreAccento ?? null;

  return (
    <SocietaContext.Provider value={{
      societaAttiva,
      listaSocieta,
      setSocietaAttiva,
      isSocietaFissa: listaSocieta.length === 1,
      loading: loading || permLoading,
      coloreAccento,
      showLoginConfirm,
      consumeLoginConfirm,
      richiediCambioSocieta,
      pendingSocieta,
      pendingSocietaActive,
      confermaCambio,
      annullaCambio,
      bloccoContesto,
      registraBloccoContesto,
      rimuoviBloccoContesto,
    }}>
      {children}
    </SocietaContext.Provider>
  );
}

export function useSocieta() {
  return useContext(SocietaContext);
}
